import { format } from 'date-fns/format';
import { EllipsisVertical } from 'lucide-react';
import { useState } from 'react';

import { type Event } from '~entities/student/modal/student';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import { DropEventDialog } from '~shared/components/dialog/drop-event-dialog';
import {
  formatClassTime,
  parseToNoonDate,
} from '~shared/helpers/datetime';
import { cn } from '~shared/shadcn/lib/utils';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import { Card, CardContent, CardTitle } from '~shared/shadcn/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~shared/shadcn/ui/dropdown-menu';

export interface EventDetailsCardProps {
  event: Event;
  studentId: string;
  studioId: string;
}

/** Card for a student's event enrollment, mirroring the class card layout. */
export function EventDetailsCard({
  event,
  studentId,
  studioId,
}: EventDetailsCardProps) {
  const { hasAdminAccess } = useCurrentUserRole();
  const [showDropDialog, setShowDropDialog] = useState(false);

  const details = event.eventId;
  const eventTitle = details?.title ?? 'Event';
  const teacherName = details?.mainTeacher ?? '-';
  const startDateString = details?.startDate
    ? parseToNoonDate(details.startDate)
    : null;
  const endDateString = details?.endDate
    ? parseToNoonDate(details.endDate)
    : null;
  const enrolledDateValue = event.eventDate ?? event.enrolledDate;
  const enrolledDateString = enrolledDateValue
    ? parseToNoonDate(enrolledDateValue)
    : null;
  const formattedStart = formatClassTime(details?.startTime);
  const formattedEnd = formatClassTime(details?.endTime);
  const time =
    formattedStart && formattedEnd ? `${formattedStart} - ${formattedEnd}` : '-';

  return (
    <>
      <Card
        className={cn('min-h-[19.625rem] w-full max-w-full md:w-[21.125rem]')}
      >
        <CardContent className='mt-6'>
          <div className='flex items-start justify-between gap-2'>
            <div className='min-w-0 flex-1'>
              {details?.isArchive === true ? (
                <Badge
                  variant='secondary'
                  className='mb-2 w-fit text-xs font-normal'
                >
                  Archived event
                </Badge>
              ) : null}
              <CardTitle
                className={cn(
                  'mb-2 text-lg leading-[1.688rem]',
                  details?.isArchive !== true && 'mt-3.5',
                )}
              >
                {eventTitle}
              </CardTitle>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger disabled={!hasAdminAccess} asChild>
                <Button variant='ghost' className='px-2.5'>
                  <EllipsisVertical />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className='w-56' align='start'>
                <DropdownMenuLabel>Event Options</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem
                    onClick={() => {
                      setShowDropDialog(true);
                    }}
                  >
                    Drop Event
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <p className={cn('mb-2 text-sm leading-[1.313rem] text-black')}>
            {time}
          </p>
          <ul className={cn('flex gap-2.5')}>
            {details?.days.map((day) => (
              <li key={day}>
                <Badge
                  className={cn('flex items-center justify-center px-1 text-xs')}
                >
                  {day}
                </Badge>
              </li>
            ))}
            {details?.oneTime === true && (
              <li>
                <Badge
                  className={cn('flex items-center justify-center px-1 text-xs')}
                >
                  One Time
                </Badge>
              </li>
            )}
          </ul>

          <div className={cn('mt-6 flex justify-between')}>
            <div className={cn('flex flex-col gap-6')}>
              <div className={cn('flex flex-col gap-1')}>
                <h4 className={cn('text-sm font-semibold leading-[1.313rem]')}>
                  Main Teacher
                </h4>
                <p className={cn('text-sm leading-[1.313rem]')}>
                  {teacherName}
                </p>
              </div>
              <div className={cn('flex flex-col gap-1')}>
                <h4 className={cn('text-sm font-semibold leading-[1.313rem]')}>
                  Event Starts
                </h4>
                <p className={cn('text-sm leading-[1.313rem]')}>
                  {startDateString
                    ? format(startDateString, 'MMMM d, yyyy')
                    : '-'}
                </p>
              </div>
            </div>

            <div className={cn('flex flex-col gap-6')}>
              <div className={cn('flex flex-col gap-1')}>
                <h4 className={cn('text-sm font-semibold leading-[1.313rem]')}>
                  Enrolled Date
                </h4>
                <p className={cn('text-sm leading-[1.313rem]')}>
                  {enrolledDateString
                    ? format(enrolledDateString, 'MMMM d, yyyy')
                    : '-'}
                </p>
              </div>
              <div className={cn('flex flex-col gap-1')}>
                <h4 className={cn('text-sm font-semibold leading-[1.313rem]')}>
                  Event End
                </h4>
                <p className={cn('text-sm leading-[1.313rem]')}>
                  {endDateString ? format(endDateString, 'MMMM d, yyyy') : '-'}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {showDropDialog && details?._id && (
        <DropEventDialog
          open={showDropDialog}
          onOpenChange={setShowDropDialog}
          eventId={details._id}
          students={[{ studentId, studioId }]}
        />
      )}
    </>
  );
}
